import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Save, Eye, Mail, ChevronDown, Plus, Send, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → Email templates.
 *
 * Every outbound customer-facing email is now editable here. Two kinds:
 *   • `system` - wired into a specific send function (PDF report, specialist
 *                enquiry alert, Day-2 drip, Day-5 drip). Can be edited; Delete
 *                resets it to the seeded default.
 *   • `custom` - admin-created draft/broadcast templates. Edit + delete freely.
 *
 * Backend:
 *   GET    /api/admin/drip-templates                       - list all
 *   POST   /api/admin/drip-templates                       - create custom
 *   PUT    /api/admin/drip-templates/{key}                 - update any
 *   DELETE /api/admin/drip-templates/{key}                 - delete custom / reset system
 *   POST   /api/admin/drip-templates/{key}/preview         - render w/ sample data
 *   POST   /api/admin/drip-templates/{key}/send-test       - send live test email
 */
export default function AdminEmailTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const load = async (preserveKey) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/drip-templates");
      setItems(data || []);
      const target = preserveKey || data?.[0]?.key;
      if (target) {
        const next = (data || []).find((d) => d.key === target) || data?.[0];
        if (next) {
          setActiveKey(next.key);
          setDraft({
            subject: next.subject || "",
            heading: next.heading || "",
            body: next.body || "",
            cta_label: next.cta_label || "",
            cta_url: next.cta_url || "",
          });
        }
      }
    } catch {
      toast.error("Couldn't load email templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const activeItem = items.find((it) => it.key === activeKey);

  const switchTo = (key) => {
    const it = items.find((x) => x.key === key);
    if (!it) return;
    setActiveKey(key);
    setDraft({
      subject: it.subject || "",
      heading: it.heading || "",
      body: it.body || "",
      cta_label: it.cta_label || "",
      cta_url: it.cta_url || "",
    });
    setPreview(null);
    setPreviewOpen(false);
  };

  const save = async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/drip-templates/${activeKey}`, draft);
      setItems(items.map((it) => (it.key === activeKey ? data : it)));
      toast.success("Template saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async () => {
    if (!activeKey) return;
    setPreviewing(true);
    try {
      await api.put(`/admin/drip-templates/${activeKey}`, draft);
      const { data } = await api.post(`/admin/drip-templates/${activeKey}/preview`);
      setPreview(data);
      setPreviewOpen(true);
    } catch {
      toast.error("Couldn't render preview.");
    } finally {
      setPreviewing(false);
    }
  };

  const sendTest = async () => {
    if (!activeKey) return;
    const to = (testEmail || "").trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid email to send the test to.");
      return;
    }
    setSendingTest(true);
    try {
      await api.put(`/admin/drip-templates/${activeKey}`, draft);
      const { data } = await api.post(`/admin/drip-templates/${activeKey}/send-test`, { to });
      toast.success(`Test email sent to ${data?.to || to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send test.");
    } finally {
      setSendingTest(false);
    }
  };

  const onDelete = async () => {
    if (!activeItem) return;
    const isSystem = activeItem.kind === "system";
    const verb = isSystem ? "reset to default" : "delete";
    if (!window.confirm(`${isSystem ? "Reset" : "Delete"} this template? This will ${verb}.`)) return;
    try {
      await api.delete(`/admin/drip-templates/${activeKey}`);
      toast.success(isSystem ? "Template reset to default." : "Template deleted.");
      await load(isSystem ? activeKey : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete.");
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-[#447694]"><Loader2 size={24} className="animate-spin inline-block" /></div>;
  }

  return (
    <div className="p-6 md:p-10" data-testid="admin-email-templates-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="bnp-chip bnp-chip-light">Email templates</span>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Outgoing emails</h1>
          <p className="text-sm text-[#447694] mt-1 max-w-2xl">
            Edit every email this site sends - and add new ones. Use <code className="bg-[#f7f7f7] px-1.5 py-0.5 rounded font-mono-brand text-xs">{`{first_name}`}</code>, <code className="bg-[#f7f7f7] px-1.5 py-0.5 rounded font-mono-brand text-xs">{`{business_name}`}</code> and any other variables shown in the sidebar. Lines starting with <code className="bg-[#f7f7f7] px-1.5 py-0.5 rounded font-mono-brand text-xs">- </code> render as bullets.
          </p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary text-sm" data-testid="email-template-new-button">
          <Plus size={14} /> New template
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-1 border-b border-[#9ac7f7]/30 overflow-x-auto" role="tablist">
        {items.map((it) => (
          <button
            key={it.key}
            onClick={() => switchTo(it.key)}
            role="tab"
            aria-selected={activeKey === it.key}
            className={`px-4 py-2 -mb-px border-b-2 font-display font-semibold text-sm transition-colors flex items-center gap-2 whitespace-nowrap ${activeKey === it.key ? "border-[#04273d] text-[#04273d]" : "border-transparent text-[#447694] hover:text-[#04273d]"}`}
            data-testid={`drip-tab-${it.key}`}
          >
            <Mail size={14} /> {it.label || it.key}
            {it.kind === "custom" && (
              <span className="ml-1 bnp-chip bnp-chip-teal text-[9px] py-0 px-1.5">Custom</span>
            )}
          </button>
        ))}
      </div>

      {activeItem && (
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Editor */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bnp-card p-5 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-display font-semibold text-[#447694] tracking-widest uppercase">About this email</p>
                <p className="text-sm text-[#04273d] mt-1.5">{activeItem.description || "Custom template - no description."}</p>
                {activeItem.updated_at && (
                  <p className="text-xs text-[#447694] mt-2">Last saved: {new Date(activeItem.updated_at).toLocaleString("en-GB")}</p>
                )}
              </div>
              <button
                onClick={onDelete}
                className="text-xs flex items-center gap-1 text-rose-700 hover:text-rose-900 font-display font-semibold shrink-0"
                data-testid="email-template-delete-button"
              >
                <Trash2 size={13} /> {activeItem.kind === "system" ? "Reset to default" : "Delete"}
              </button>
            </div>
            <div className="bnp-card p-5 space-y-4">
              <div>
                <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Subject line</label>
                <input
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="bnp-input"
                  data-testid="drip-subject-input"
                />
              </div>
              <div>
                <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Heading (top of email body)</label>
                <input
                  value={draft.heading}
                  onChange={(e) => setDraft({ ...draft, heading: e.target.value })}
                  className="bnp-input"
                  data-testid="drip-heading-input"
                />
              </div>
              <div>
                <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Body</label>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={18}
                  className="bnp-input font-mono-brand text-sm"
                  placeholder={"Hi {first_name},\n\nA couple of days ago you ran a free name check on {business_name}.\n\n- Bullet one\n- Bullet two\n\nAll the best,\nThe NBR Group team"}
                  data-testid="drip-body-input"
                />
                <p className="text-xs text-[#447694] mt-2">
                  Blank lines start a new paragraph. Lines beginning with <code className="bg-[#f7f7f7] px-1.5 py-0.5 rounded font-mono-brand text-xs">- </code> become bullets.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">CTA button label (optional)</label>
                  <input
                    value={draft.cta_label}
                    onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })}
                    placeholder="See plans"
                    className="bnp-input"
                    data-testid="drip-cta-label-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">CTA button URL</label>
                  <input
                    value={draft.cta_url}
                    onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })}
                    placeholder="https://protectmybusinessname.co.uk/pricing"
                    className="bnp-input"
                    data-testid="drip-cta-url-input"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="drip-save-button">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}
                </button>
                <button onClick={doPreview} disabled={previewing} className="btn-secondary text-sm" data-testid="drip-preview-button">
                  {previewing ? <><Loader2 size={14} className="animate-spin" /> Rendering…</> : <><Eye size={14} /> Preview with sample data</>}
                </button>
              </div>
            </div>

            {/* Send test */}
            <div className="bnp-card p-5" data-testid="email-template-send-test-card">
              <p className="font-display font-bold text-[#04273d] flex items-center gap-2"><Send size={15} /> Send a live test</p>
              <p className="text-xs text-[#447694] mt-1 mb-3">Renders the template with sample data and sends a real email - useful before going live with copy changes.</p>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.co.uk"
                  className="bnp-input flex-1 min-w-[220px]"
                  data-testid="email-template-test-input"
                />
                <button
                  onClick={sendTest}
                  disabled={sendingTest}
                  className="btn-primary text-sm"
                  data-testid="email-template-send-test-button"
                >
                  {sendingTest ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send test</>}
                </button>
              </div>
            </div>
          </div>

          {/* Variables sidebar + preview */}
          <aside className="space-y-4">
            <div className="bnp-card p-5">
              <p className="font-display font-bold text-[#04273d]">Variables you can use</p>
              <p className="text-xs text-[#447694] mt-1 mb-3">Click to copy - paste into subject, heading, body, or CTA URL.</p>
              <div className="flex flex-wrap gap-2" data-testid="drip-variables-list">
                {(activeItem.variables_available || ["{first_name}", "{business_name}"]).map((v) => (
                  <button
                    key={v}
                    onClick={() => { navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); }}
                    className="bnp-chip bnp-chip-light text-[11px] font-mono-brand cursor-pointer hover:bg-[#04273d] hover:text-white transition-colors"
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {preview && previewOpen && (
              <div className="bnp-card p-5" data-testid="drip-preview-card">
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold text-[#04273d]">Preview</p>
                  <button onClick={() => setPreviewOpen(false)} className="text-[#447694] hover:text-[#04273d]" aria-label="Close preview">
                    <ChevronDown size={18} />
                  </button>
                </div>
                <p className="text-xs text-[#447694] mt-1">Sample data: Sophie, Acme Trading Ltd, consultancies</p>
                <div className="mt-4 space-y-3">
                  <div>
                    <p className="text-xs font-display font-semibold text-[#447694] tracking-widest uppercase">Subject</p>
                    <p className="text-sm text-[#04273d] mt-1 font-semibold">{preview.subject}</p>
                  </div>
                  <div>
                    <p className="text-xs font-display font-semibold text-[#447694] tracking-widest uppercase">Heading</p>
                    <p className="text-sm text-[#04273d] mt-1 font-semibold">{preview.heading}</p>
                  </div>
                  <div>
                    <p className="text-xs font-display font-semibold text-[#447694] tracking-widest uppercase">Body</p>
                    <pre className="text-xs text-[#04273d] mt-1 whitespace-pre-wrap font-display leading-relaxed">{preview.body}</pre>
                  </div>
                  {preview.cta_label && (
                    <div>
                      <p className="text-xs font-display font-semibold text-[#447694] tracking-widest uppercase">CTA</p>
                      <p className="text-sm text-[#04273d] mt-1">
                        <span className="bnp-chip bnp-chip-teal">{preview.cta_label}</span>
                        <span className="text-xs text-[#447694] ml-2 font-mono-brand break-all">{preview.cta_url}</span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* "New template" modal */}
      {showNew && (
        <NewTemplateModal
          onClose={() => setShowNew(false)}
          onCreated={async (key) => { setShowNew(false); await load(key); }}
        />
      )}
    </div>
  );
}


function NewTemplateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    key: "",
    label: "",
    description: "",
    subject: "",
    heading: "",
    body: "",
    cta_label: "",
    cta_url: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/drip-templates", form);
      toast.success("Template created.");
      onCreated(data?.key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto bg-black/0" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-lg p-7 relative bnp-hero-shadow my-auto max-h-[calc(100vh-2rem)] overflow-y-auto"
        data-testid="email-template-new-modal"
      >
        <button type="button" onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[#9ac7f7]/20 text-[#447694]" aria-label="Close">
          <X size={18} />
        </button>
        <h3 className="font-display text-2xl text-[#04273d] font-bold tracking-tight">New email template</h3>
        <p className="text-sm text-[#447694] mt-2 mb-5">Custom templates aren&rsquo;t wired to a send trigger yet &mdash; they&rsquo;re available as drafts and can be sent via &ldquo;Send a live test&rdquo;.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Key (lowercase, underscores)</label>
            <input
              required
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value })}
              placeholder="e.g. welcome_v2"
              className="bnp-input font-mono-brand text-sm"
              data-testid="email-template-new-key"
            />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Display label</label>
            <input
              required
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Welcome message (v2)"
              className="bnp-input"
              data-testid="email-template-new-label"
            />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Subject line</label>
            <input
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Welcome to Business Name Protection"
              className="bnp-input"
            />
          </div>
          <div>
            <label className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-1.5">Body</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={6}
              className="bnp-input font-mono-brand text-sm"
              placeholder={"Hi {first_name},\n\nWelcome aboard - your business name is now protected."}
            />
          </div>
        </div>
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-5" data-testid="email-template-new-submit">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><Plus size={14} /> Create template</>}
        </button>
      </form>
    </div>
  );
}
